import { RUN_MODE, DRIVE, MSG_WORKER, MSG_MAIN,
  MouseEventSimple, default6502State, TEST_DEBUG, 
  DEFAULT_SLOT_CONFIG, VeraSdStatus, 
  MEMORY_DUMP_STATE,
  AUTO_SNAPSHOT,
  ERROR_CODE} from "../common/utility"
import { getStartupTextPage } from "./panels/help/startuptextpage"
import { doRumble } from "./devices/gamepad"
import { BreakpointMap } from "../common/breakpoint"
import { copyCanvas } from "./copycanvas"
import { set6502Instructions } from "./panels/disassembly/disassembly_utilities"
import { doSetUIDriveProps } from "./devices/disk/driveprops"
import { setEnhancedMidi } from "./devices/audio/enhancedmidi"
import { receiveMidiData } from "./devices/audio/midiinterface"
import { playMockingboard } from "./devices/audio/mockingboard_audio"
import { playVeraFmReset, playVeraFmWrite, playVeraPcmWrite, playVeraPsgWrite, playVeraPsgBatch } from "./devices/audio/vera_psg_audio"
import { emulatorSoundEnable, clickSpeaker } from "./devices/audio/speaker"
import { doPlayDriveSound } from "./devices/disk/drivesounds"
import { receiveCommData } from "./devices/serial/serialhub"
import { Buffer } from "buffer"
import { setDialogMessageContext } from "./ui_utilities"

let worker: Worker | null = null

let saveStateCallback: (sState: EmulatorSaveState) => void
let bootCallback: (() => void) | null = null
let serialConfigCallback: ((config: SerialConfig) => void) | null = null
let executionStateCallback: ((execution: ExecutionSnapshot) => void) | null = null
let lastExecutionSequence = -1
let executionSequenceOffset = 0
let resetExecutionSequence = false
let nextWorkerOperationId = 0
const pendingWorkerOperations = new Map<number, {
  resolve: (value: MessagePayload | undefined) => void,
  reject: (error: Error) => void,
  timeout: ReturnType<typeof setTimeout>,
}>()

let veraSdStatus: VeraSdStatus = { attached: false, name: "", size: 0 }
const veraSdStatusListeners = new Set<(status: VeraSdStatus) => void>()
const veraSdImageResolvers: Array<(result: { data: Uint8Array, name: string } | null) => void> = []

const requestWorkerOperation = <T = void>(
  msg: MSG_MAIN,
  payload: MessagePayload,
  timeoutMs = 5000,
): Promise<T> => new Promise((resolve, reject) => {
  const operationId = ++nextWorkerOperationId
  const operation = {
    resolve: (value: MessagePayload | undefined) => resolve(value as T),
    reject,
    timeout: setTimeout(() => {
      pendingWorkerOperations.delete(operationId)
      reject(new Error("Timed out waiting for worker operation"))
    }, timeoutMs),
  }
  pendingWorkerOperations.set(operationId, operation)
  doPostMessage(msg, payload, operationId)
})

const resolveWorkerOperation = ({ operationId, error, value }: WorkerOperationResult) => {
  const operation = pendingWorkerOperations.get(operationId)
  if (!operation) return
  clearTimeout(operation.timeout)
  pendingWorkerOperations.delete(operationId)
  if (error) {
    operation.reject(new Error(error))
  } else {
    operation.resolve(value)
  }
}

export const setMain2Worker = (workerIn: Worker) => {
  resetExecutionSequence = worker !== null && worker !== workerIn
  worker = workerIn
  doPostMessage(MSG_MAIN.VERA_FM_ENABLED, localStorage.getItem("veraFmAudioEnabled") !== "false")
  machineState.execution = undefined
}

export const setBootCallback = (callback: () => void) => {
  bootCallback = callback
}

export const setSerialConfigCallback = (callback: (config: SerialConfig) => void) => {
  serialConfigCallback = callback
}

export const setExecutionStateCallback = (callback: (execution: ExecutionSnapshot) => void) => {
  executionStateCallback = callback
  if (machineState.execution) callback(machineState.execution)
}

const doPostMessage = (msg: MSG_MAIN, payload: MessagePayload, operationId?: number) => {
  if (!worker) return
  worker.postMessage(operationId === undefined ? {msg, payload} : {msg, payload, operationId})
}

export const passSetRunMode = (runMode: RUN_MODE) => {
  if (runMode === RUN_MODE.NEED_BOOT && bootCallback) {
    bootCallback()
  }
  doPostMessage(MSG_MAIN.RUN_MODE, runMode)
  machineState.runMode = runMode
}

export const requestSetRunMode = (runMode: RUN_MODE, timeoutMs = 5000) => {
  if (runMode === RUN_MODE.NEED_BOOT && bootCallback) {
    bootCallback()
  }
  machineState.runMode = runMode
  return requestWorkerOperation(MSG_MAIN.RUN_MODE, runMode, timeoutMs)
}

export const passSetCyclesToRun = (cycles: number) => {
  doPostMessage(MSG_MAIN.CYCLES_TO_RUN, cycles)
}

export const passSetState6502 = (state: STATE6502) => {
  doPostMessage(MSG_MAIN.STATE6502, state)
}

export const requestSetState6502 = (state: STATE6502, timeoutMs = 5000) => {
  return requestWorkerOperation(MSG_MAIN.STATE6502, state, timeoutMs)
}

export const passBreakpoints = (breakpoints: BreakpointMap) => {
  doPostMessage(MSG_MAIN.BREAKPOINTS, breakpoints)
  // Force the state right away, so the UI can update.
  machineState.breakpoints = breakpoints
}

export const passStepInto = () => {
  doPostMessage(MSG_MAIN.STEP_INTO, true)
}

export const passStepOver = () => {
  doPostMessage(MSG_MAIN.STEP_OVER, true)
}

export const passStepOut = () => {
  doPostMessage(MSG_MAIN.STEP_OUT, true)
}

export const passAutoSnapshot = (payload: AUTO_SNAPSHOT) => {
  doPostMessage(MSG_MAIN.AUTO_SNAPSHOT, payload)
}

export const passBasicStep = () => {
  doPostMessage(MSG_MAIN.BASIC_STEP, true)
}

export const passSetIsDebugging = (doDebug: boolean) => {
  doPostMessage(MSG_MAIN.DEBUG, doDebug)
  // Force the state right away, so the UI can update.
  machineState.isDebugging = doDebug
}

export const passSetAppMode = (mode: string) => {
  doPostMessage(MSG_MAIN.APP_MODE, mode)
}

export const passSetShowDebugTab = (show: boolean) => {
  doPostMessage(MSG_MAIN.SHOW_DEBUG_TAB, show)
  // Force the state right away, so the UI can update.
  machineState.showDebugTab = show
}

export const passSpeedMode = (mode: number) => {
  doPostMessage(MSG_MAIN.SPEED, mode)
  // Force the state right away, so the UI can update.
  machineState.speedMode = mode
}

export const requestSpeedMode = (mode: number, timeoutMs = 5000) => {
  machineState.speedMode = mode
  return requestWorkerOperation(MSG_MAIN.SPEED, mode, timeoutMs)
}

export const passGoForwardInTime = () => {
  doPostMessage(MSG_MAIN.TIME_TRAVEL_STEP, "FORWARD")
}

export const passGoBackInTime = () => {
  doPostMessage(MSG_MAIN.TIME_TRAVEL_STEP, "BACKWARD")
}

export const passTimeTravelIndex = (index: number) => {
  doPostMessage(MSG_MAIN.TIME_TRAVEL_INDEX, index)
}

export const passTimeTravelSnapshot = () => {
  doPostMessage(MSG_MAIN.TIME_TRAVEL_SNAPSHOT, true)
}

export const passRestoreSaveState = (sState: EmulatorSaveState) => {
  doPostMessage(MSG_MAIN.RESTORE_STATE, sState)
}

export const passKeypress = (key: number) => {
  doPostMessage(MSG_MAIN.KEYPRESS, key)
}

export const passKeyboardState = (payload: KeyboardState) => {
  doPostMessage(MSG_MAIN.KEYBOARD_STATE, payload)
}

export const requestKeyboardState = (payload: KeyboardState, timeoutMs = 5000) => {
  return requestWorkerOperation(MSG_MAIN.KEYBOARD_STATE, payload, timeoutMs)
}

export const requestKeySequence = (payload: KeySequenceRequest) => {
  const timeoutMs = Number.isInteger(payload.timeoutMs)
    && payload.timeoutMs >= 1
    && payload.timeoutMs <= 120000
    ? payload.timeoutMs
    : 0
  return requestWorkerOperation<KeySequenceResult>(
    MSG_MAIN.KEY_SEQUENCE,
    payload,
    timeoutMs + 1000,
  )
}

export const requestConditionalKeySequence = (payload: ConditionalKeySequenceRequest) => {
  const timeoutMs = Number.isFinite(payload.timeoutMs)
    ? Math.max(1, payload.timeoutMs + 1000)
    : 1000
  return requestWorkerOperation<ConditionalKeySequenceResult>(
    MSG_MAIN.CONDITIONAL_KEY_SEQUENCE,
    payload,
    timeoutMs,
  )
}

export const requestCancelConditionalKeySequence = (timeoutMs = 5000) =>
  requestWorkerOperation<{cancelled: boolean}>(
    MSG_MAIN.CANCEL_CONDITIONAL_KEY_SEQUENCE,
    {},
    timeoutMs,
  )

export const passKeyRelease = () => {
  setTimeout(() => {
    // Delay the key release to give the emulator time to process the keypress
    doPostMessage(MSG_MAIN.KEYRELEASE, true)}, 50)
}

export const passSingleKeyPress = (key: number) => {
  passKeyboardState({
    key: key,
    isDown: true,
    repeat: false,
  })
  setTimeout(() => {passKeyboardState({
    key: 0x13,
    isDown: false,
    repeat: false,
  })}, 50)
}

export const passMouseEvent = (event: MouseEventSimple) => {
  doPostMessage(MSG_MAIN.MOUSEEVENT, event)
}

export const passPasteText = (text: string) => {
  text = text.replaceAll(/[”“]/g,"\"")  // fancy quotes with regular
  text = text.replaceAll("\n","\r")  // LFs to CRs
  doPostMessage(MSG_MAIN.PASTE_TEXT, text)
}

export const passAppleCommandKeyPress = (left: boolean) => {
  if (left) {
    machineState.button0 = true
  } else {
    machineState.button1 = true
  }
  doPostMessage(MSG_MAIN.APPLE_PRESS, left)
}

export const passAppleCommandKeyRelease = (left: boolean) => {
  if (left) {
    machineState.button0 = false
  } else {
    machineState.button1 = false
  }
  doPostMessage(MSG_MAIN.APPLE_RELEASE, left)
}

export const passSetGamepads = (gamePads: EmuGamepad[] | null) => {
  if (machineState.runMode !== RUN_MODE.IDLE) {
    doPostMessage(MSG_MAIN.GAMEPAD, gamePads)
  }
}

export const passSetBinaryBlock = (address: number, data: Uint8Array, run = false) => {
  const memBlock: SetMemoryBlock = {address, data, run}
  doPostMessage(MSG_MAIN.SET_BINARY_BLOCK, memBlock)
}

export const passRunBinary = (address: number, data: Uint8Array, entryAddress = address) => {
  const binary: RunBinary = {address, data, entryAddress}
  doPostMessage(MSG_MAIN.RUN_BINARY, binary)
}

export const passLoadBinary = (address: number, data: Uint8Array) => {
  const binary: LoadBinary = {address, data}
  doPostMessage(MSG_MAIN.LOAD_BINARY, binary)
}

export const requestLoadBinary = (
  address: number,
  data: Uint8Array,
  timeoutMs = 5000,
) => {
  const binary: LoadBinary = {address, data}
  return requestWorkerOperation(MSG_MAIN.LOAD_BINARY, binary, timeoutMs)
}

export const passExecuteBasicCommand = (command: string) => {
  doPostMessage(MSG_MAIN.EXECUTE_BASIC_COMMAND, command)
}

export const passSetCycleCount = (count: number) => {
  doPostMessage(MSG_MAIN.SET_CYCLECOUNT, count)
}

export const passSetMemory = (address: number, value: number) => {
  doPostMessage(MSG_MAIN.SET_MEMORY, {address, value})
}

export const requestWriteMemory = (address: number, data: Uint8Array, timeoutMs = 5000) => {
  return requestWorkerOperation(MSG_MAIN.WRITE_MEMORY, {address, data}, timeoutMs)
}

export const passRxCommData = (data: Uint8Array) => {
  doPostMessage(MSG_MAIN.COMM_DATA, data)
}

export const passRxMidiData = (data: Uint8Array) => {
  doPostMessage(MSG_MAIN.MIDI_DATA, data)
}

const passThumbnailImage = (thumbnail: string) => {
  doPostMessage(MSG_MAIN.THUMBNAIL_IMAGE, thumbnail)
}

export const passReverseYAxis = (mode: boolean) => {
  doPostMessage(MSG_MAIN.REVERSE_YAXIS, mode)
}

export const passSetRamWorks = (size: number) => {
  doPostMessage(MSG_MAIN.RAMWORKS, size)
  // This will also come from the emulator, but set it here so the UI updates
  // if the emulator hasn't been booted yet.
  machineState.extraRamSize = size
}

export const passSetMachineName = (name: MACHINE_NAME) => {
  doPostMessage(MSG_MAIN.MACHINE_NAME, name)
  // This will also come from the emulator, but set it here so the UI updates
  // if the emulator hasn't been booted yet.
  machineState.machineName = name
}

export const passSetVeraFmEnabled = (enabled: boolean) => {
  doPostMessage(MSG_MAIN.VERA_FM_ENABLED, enabled)
}

export const passSetVeraSlot = (slot: VERA_SLOT) => {
  doPostMessage(MSG_MAIN.VERA_SLOT, slot)
  // This will also come from the emulator, but set it here so the UI updates
  // if the emulator hasn't been booted yet.
  machineState.veraSlot = slot
}

export const passSetSlotConfig = (config: SlotConfig) => {
  doPostMessage(MSG_MAIN.SLOT_CONFIG, config)
  machineState.slotConfig = config
  window.dispatchEvent(new CustomEvent("apple2ts-slot-config-changed", { detail: config }))
}

export const passSetSoftSwitches = (addresses: Array<number> | null) => {
  doPostMessage(MSG_MAIN.SOFTSWITCHES, addresses)
}

export const passSetVideo7Override = (mode: Video7Mode, enabled: boolean) => {
  doPostMessage(MSG_MAIN.VIDEO7_OVERRIDE, {mode, enabled})
}

export const passSetDriveNewData = (props: DriveProps, forceIndex = false) => {
  doPostMessage(MSG_MAIN.DRIVE_NEW_DATA, forceIndex ? { props, forceIndex } : props)
}

export const requestSetDriveNewData = (props: DriveProps, forceIndex = false, timeoutMs = 5000) => {
  return requestWorkerOperation(
    MSG_MAIN.DRIVE_NEW_DATA,
    forceIndex ? { props, forceIndex } : props,
    timeoutMs,
  )
}

export const passSetDriveProps = (props: DriveProps) => {
  doPostMessage(MSG_MAIN.DRIVE_PROPS, props)
}

export const passSiriusJoyport = (mode: boolean) => {
  doPostMessage(MSG_MAIN.SIRIUS_JOYPORT, mode)
}

export const passSetTracing = (tracing: boolean) => {
  doPostMessage(MSG_MAIN.TRACING, tracing)
  // Force the state right away, so the UI can update.
  machineState.isTracing = tracing
}

export const passSetTraceSettings = (traceSettings: TraceSettings) => {
  doPostMessage(MSG_MAIN.TRACE_SETTINGS, traceSettings)
}

export const passRequestMemoryDump = () => {
  doPostMessage(MSG_MAIN.GET_MEMORY_REQUEST, true)
}

export const passMemoryDumpState = (state: MEMORY_DUMP_STATE) => {
  doPostMessage(MSG_MAIN.MEMORY_DUMP_STATE, state)
}

export const passEnableGamePatches = (enabled: boolean) => {
  doPostMessage(MSG_MAIN.ENABLE_GAME_PATCHES, enabled)
}

export const requestMemoryView = (
  request: MemoryViewRequest,
  timeoutMs = 5000,
) => requestWorkerOperation<MemoryView>(MSG_MAIN.GET_MEMORY_VIEW, request, timeoutMs)

export const requestMemorySearch = (
  request: MemorySearchRequest,
  timeoutMs = 5000,
) => requestWorkerOperation<MemorySearchResult>(MSG_MAIN.FIND_MEMORY, request, timeoutMs)

export const requestSetMemoryWriteWatchpoint = (
  request: MemoryViewRequest,
  timeoutMs = 5000,
) => requestWorkerOperation<MemoryWriteWatchpoint & {executionSequence: number}>(
  MSG_MAIN.SET_MEMORY_WRITE_WATCHPOINT,
  request,
  timeoutMs,
)

export const requestClearMemoryWriteWatchpoint = (timeoutMs = 5000) =>
  requestWorkerOperation<{cleared: boolean}>(MSG_MAIN.CLEAR_MEMORY_WRITE_WATCHPOINT, {}, timeoutMs)

export const requestCreateSessionSnapshot = (snapshotId: string, timeoutMs = 5000) =>
  requestWorkerOperation<SessionSnapshotReceipt>(MSG_MAIN.CREATE_SESSION_SNAPSHOT, snapshotId, timeoutMs)

export const requestSessionMemoryComparison = (request: SessionMemoryComparisonRequest, timeoutMs = 5000) =>
  requestWorkerOperation<SessionMemoryComparison>(MSG_MAIN.COMPARE_SESSION_MEMORY, request, timeoutMs)

export const requestRestoreSessionSnapshot = (snapshotId: string, timeoutMs = 5000) =>
  requestWorkerOperation<SessionSnapshotReceipt>(MSG_MAIN.RESTORE_SESSION_SNAPSHOT, snapshotId, timeoutMs)

// This is a cached memory dump, updated whenever the main requests a new one.
// Currently only used by the AI Agent, since it may want to look at memory
// even when the emulator is not paused.
let memoryResource: Uint8Array<ArrayBuffer> = new Uint8Array()

let machineState: MachineState = {
  addressGetTable: new Uint32Array(),
  altChar: true,
  basicMemory: new Uint8Array(),
  breakpoints: new BreakpointMap(),
  button0: false,
  button1: false,
  c800Slot: 255,
  canGoBackward: true,
  canGoForward: true,
  cout: 0,
  cpuSpeed: 0,
  currentMemory: new Uint8Array(),
  extraRamSize: 64,
  heatMapCPU: new Float64Array(),
  heatMapCPUMax: { value: 0, index: 0 },
  heatMapGetMem: new Float64Array(),
  heatMapGetMemMax: { value: 0, index: 0 },
  heatMapSetMem: new Float64Array(),
  heatMapSetMemMax: { value: 0, index: 0 },
  hires: new Uint8Array(),
  isDebugging: TEST_DEBUG,
  isTracing: TEST_DEBUG,
  iTempState: 0,
  lores: new Uint8Array(),
  machineName: "APPLE2EE",
  memoryDump: new Uint8Array(),
  noDelayMode: false,
  ramWorksBank: 0,
  runMode: RUN_MODE.IDLE,
  s6502: default6502State(),
  showDebugTab: false,
  slotConfig: { ...DEFAULT_SLOT_CONFIG },
  speedMode: 0,
  softSwitches: {} as unknown as MachineState["softSwitches"],
  stackString: "",
  textPage: new Uint8Array(1).fill(32),
  timeTravelThumbnails: new Array<TimeTravelThumbnail>(),
  tracelog: new Array<string>(),
  veraSlot: 0,
  zeroPage: new Uint8Array(256).fill(255)
}

export const doOnMessage = (e: MessageEvent): {speed: number, helptext: string} | null => {
  switch (e.data.msg as MSG_WORKER) {
    case MSG_WORKER.MACHINE_STATE: {
      const newState = e.data.payload as MachineState
      if (machineState.runMode !== newState.runMode) {
        emulatorSoundEnable(newState.runMode === RUN_MODE.RUNNING)
      }
      let execution = newState.execution
      if (execution) {
        if (resetExecutionSequence) {
          executionSequenceOffset = lastExecutionSequence + 1 - execution.executionSequence
          resetExecutionSequence = false
        }
        execution = {
          ...execution,
          executionSequence: execution.executionSequence + executionSequenceOffset,
        }
      }
      machineState = {...newState, execution}
      if (execution && execution.executionSequence > lastExecutionSequence) {
        lastExecutionSequence = execution.executionSequence
        executionStateCallback?.(execution)
      }
      return {speed: machineState.cpuSpeed, helptext: ""}
    }
    case MSG_WORKER.OPERATION_RESULT:
      resolveWorkerOperation(e.data.payload as WorkerOperationResult)
      break
    case MSG_WORKER.SAVE_STATE: {
      const sState = e.data.payload as EmulatorSaveState
      if (saveStateCallback) saveStateCallback(sState)
      break
    }
    case MSG_WORKER.CLICK:
      clickSpeaker(e.data.payload as number)
      break
    case MSG_WORKER.DRIVE_PROPS: {
      const payload = e.data.payload as DriveProps | {props: DriveProps, replaceDiskData: boolean}
      if ("props" in payload) {
        doSetUIDriveProps(payload.props, payload.replaceDiskData)
      } else {
        doSetUIDriveProps(payload)
      }
      return {speed: machineState.cpuSpeed, helptext: ""}
    }
    case MSG_WORKER.DRIVE_SOUND: {
      const sound = e.data.payload as DRIVE
      doPlayDriveSound(sound)
      break
    }
    case MSG_WORKER.RUMBLE: {
      const params = e.data.payload as GamePadActuatorEffect
      doRumble(params)
      break
    }
    case MSG_WORKER.HELP_TEXT: {
      const helptext = e.data.payload as string
      return {speed: 0, helptext: helptext}
      break
    }
    case MSG_WORKER.SHOW_APPLE_MOUSE: {
      showAppleMouse = e.data.payload as boolean
      break
    }
    case MSG_WORKER.MBOARD_SOUND: {
      const mboard = e.data.payload as MockingboardSound
      playMockingboard(mboard)
      break
    }
    case MSG_WORKER.VERA_PSG_WRITE: {
      const psgWrite = e.data.payload as VeraPsgWrite
      playVeraPsgWrite(psgWrite)
      break
    }
    case MSG_WORKER.VERA_PSG_BATCH: {
      const psgBatch = e.data.payload as VeraPsgWrite[]
      playVeraPsgBatch(psgBatch)
      break
    }
    case MSG_WORKER.VERA_PCM_WRITE: {
      const pcmWrite = e.data.payload as VeraPcmWrite
      playVeraPcmWrite(pcmWrite)
      break
    }
    case MSG_WORKER.VERA_FM_WRITE: {
      playVeraFmWrite(e.data.payload as VeraFmWrite[])
      break
    }
    case MSG_WORKER.VERA_FM_RESET: {
      playVeraFmReset()
      break
    }
    case MSG_WORKER.VERA_SD_STATUS: {
      veraSdStatus = e.data.payload as VeraSdStatus
      veraSdStatusListeners.forEach(fn => fn(veraSdStatus))
      break
    }
    case MSG_WORKER.VERA_SD_IMAGE_DATA: {
      const payload = e.data.payload as { data: Uint8Array, name: string, writeSeq?: number } | null
      const resolver = veraSdImageResolvers.shift()
      if (resolver) resolver(payload)
      break
    }
    case MSG_WORKER.COMM_DATA: {
      const commdata = e.data.payload as Uint8Array
      receiveCommData(commdata)
      break
    }
    case MSG_WORKER.MIDI_DATA: {
      const mididata = e.data.payload as Uint8Array
      receiveMidiData(mididata)
      break
    }
    case MSG_WORKER.ENHANCED_MIDI: {
      const midiarg = e.data.payload as number
      setEnhancedMidi(midiarg)
      break
    }
    case MSG_WORKER.REQUEST_THUMBNAIL: {
      copyCanvas((blob) => {
        const reader = new FileReader()
        reader.onloadend = function() {
          passThumbnailImage(reader.result as string)
        }        
        reader.readAsDataURL(blob)
      }, true)
      break
    }
    case MSG_WORKER.SOFTSWITCH_DESCRIPTIONS:
      softSwitchDescriptions = e.data.payload as string[]
      break
    case MSG_WORKER.INSTRUCTIONS: {
      const instructions = e.data.payload as Array<PCodeInstr1>
      set6502Instructions(instructions)
      break
    }
    case MSG_WORKER.GET_MEMORY_RESPONSE:
      // This is a response to a GET_MEMORY_REQUEST. Update the memory dump in the state.
      memoryResource = e.data.payload as Uint8Array<ArrayBuffer>
      break
    case MSG_WORKER.SERIAL_CONFIG_CHANGE: {
      const serialConfig = e.data.payload as SerialConfig
      if (serialConfigCallback) serialConfigCallback(serialConfig)
      break
    }
    case MSG_WORKER.VERA_FRAME: {
      latestVeraFrame = e.data.payload as { fb?: Uint8ClampedArray<ArrayBuffer>, dcVideo: number }
      break
    }
    case MSG_WORKER.ERRORMSG: {
      const errorMsg = e.data.payload as ErrorMsg
      setDialogMessageContext({
        title: "Error",
        message: errorMsg.message,
        code: ERROR_CODE.ERROR,
        actionCancel: () => {},
        actionConfirm: () => {},
      })
      break
    }
    default:
      console.error("main2worker: unknown msg: " + JSON.stringify(e.data))
      break
  }
  return null
}

// Should probably store these state variables somewhere else, but it's
// easy to just stash them here.
let showAppleMouse = false
let softSwitchDescriptions = [""]
let latestVeraFrame: { fb?: Uint8ClampedArray<ArrayBuffer>, dcVideo: number } | null = null

export const handleGetVeraFrame = () => {
  return latestVeraFrame
}

export const handleGetShowAppleMouse = () => {
  const isFullscreen = document.fullscreenElement !== null
  return showAppleMouse || isFullscreen
}

export const handleGetRunMode = () => {
  return machineState.runMode
}

export const handleGetBreakpoints = () => {
  return machineState.breakpoints
}

export const handleGetExecutionBreakpoint = () => {
  return machineState.execution?.breakpoint?.address || -1
}

export const handleGetCout = () => {
  return machineState.cout
}

export const handleGetSpeedMode = () => {
  return machineState.speedMode
}

export const handleGetIsDebugging = () => {
  return machineState.isDebugging
}

export const handleGetShowDebugTab = () => {
  return machineState.showDebugTab
}

export const handleGetState6502 = () => {
  return machineState.s6502
}

export const handleGetExecution = () => {
  return machineState.execution
}

export const handleGetHeatMapCPU = () => {
  return machineState.heatMapCPU
}

export const handleGetHeatMapCPUMax = () => {
  return machineState.heatMapCPUMax
}

export const handleGetHeatMapGetMem = () => {
  return machineState.heatMapGetMem
}

export const handleGetHeatMapGetMemMax = () => {
  return machineState.heatMapGetMemMax
}

export const handleGetHeatMapSetMem = () => {
  return machineState.heatMapSetMem
}

export const handleGetHeatMapSetMemMax = () => {
  return machineState.heatMapSetMemMax
}

export const handleGetTextPage = () => {
  // Always return the intial start page if we're idle
  return(machineState.runMode === RUN_MODE.IDLE) ? getStartupTextPage(machineState.machineName) : machineState.textPage
}

export const handleGetTextPageAsString = () => {
  return Buffer.from(machineState.textPage.map((n) => (n &= 127))).toString()
}

export const handleGetLores = () => {
  return machineState.lores
}

export const handleGetHires = () => {
  return machineState.hires
}

export const handleGetShr = () => {
  return machineState.shr || new Uint8Array()
}

export const handleGetVidhdActive = () => {
  return !!machineState.vidhdActive
}

export const handleGetNoDelayMode = () => {
  return machineState.noDelayMode
}

export const handleGetAltCharSet = () => {
  return machineState.altChar
}

export const handleGetStackString = () => {
  return machineState.stackString
}

export const handleGetSoftSwitches = () => {
  return machineState.softSwitches
}

export const handleGetC800Slot = () => {
  return machineState.c800Slot
}

export const handleGetRamWorksBank = () => {
  return machineState.ramWorksBank
}

export const handleGetMemoryResource = () => {
  return memoryResource
}

export const handleGetCurrentMemory = () => {
  return machineState.currentMemory.length > 0 ? machineState.currentMemory : machineState.memoryDump
}

export const handleGetMemoryAtAddress = (addr: number) => {
  if (machineState.currentMemory.length > 0) {
    return machineState.currentMemory[addr % 0x10000]
  } else if (machineState.memoryDump.length > 0) {
    return machineState.memoryDump[addr % 0x10000]
  }
  return 0
}

export const handleGetMemoryDump = () => {
  return machineState.memoryDump
}

export const handleGetAddressGetTable = () => {
  return machineState.addressGetTable
}

export const handleGetLeftButton = () => {
  return machineState.button0
}

export const handleGetRightButton = () => {
  return machineState.button1
}

export const handleCanGoBackward = () => {
  return machineState.canGoBackward
}

export const handleCanGoForward = () => {
  return machineState.canGoForward
}

export const handleGetTempStateIndex = () => {
  return machineState.iTempState
}

export const handleGetTimeTravelThumbnails = () => {
  return machineState.timeTravelThumbnails
}

export const handleGetSaveState = (callback: (sState: EmulatorSaveState) => void,
  withSnapshots = false) => {
  saveStateCallback = callback
  doPostMessage(withSnapshots ? MSG_MAIN.GET_SAVE_STATE_SNAPSHOTS : MSG_MAIN.GET_SAVE_STATE, true)
}

export const handleGetMemSize = () => {
  return machineState.extraRamSize
}

export const handleGetMachineName = () => {
  return machineState.machineName
}

export const handleGetVeraSlot = () => {
  return machineState.veraSlot
}

export const handleGetSlotConfig = (): SlotConfig => {
  return machineState.slotConfig || { ...DEFAULT_SLOT_CONFIG }
}

export const handleGetSoftSwitchDescriptions = () => {
  return softSwitchDescriptions
}

export const handleGetZeroPage = () => {
  return machineState.zeroPage
}

export const handleGetTracing = () => {
  return machineState.isTracing
}

export const handleGetTracelog = () => {
  return machineState.tracelog
}

export const handleGetBasicMemory = () => {
  return machineState.basicMemory
}

export const handleGetVeraSdStatus = (): VeraSdStatus => {
  return veraSdStatus
}

export const subscribeVeraSdStatus = (listener: (status: VeraSdStatus) => void) => {
  veraSdStatusListeners.add(listener)
  listener(veraSdStatus)
  return () => {
    veraSdStatusListeners.delete(listener)
  }
}

export const handleSetVeraSdImage = (data: Uint8Array | null, name: string = "sd.img") => {
  doPostMessage(MSG_MAIN.VERA_SD_IMAGE, data ? { data, name } : null)
}

export const requestVeraSdImage = (): Promise<{ data: Uint8Array, name: string, writeSeq?: number } | null> => {
  return new Promise((resolve) => {
    veraSdImageResolvers.push(resolve)
    doPostMessage(MSG_MAIN.VERA_SD_GET_IMAGE, null)
  })
}

export const handleSetVeraSdWriteProtected = (wp: boolean) => {
  doPostMessage(MSG_MAIN.VERA_SD_WRITE_PROTECT, wp)
}

export const handleClearVeraSdChanges = (writeSeq?: number) => {
  doPostMessage(MSG_MAIN.VERA_SD_CLEAR_CHANGES, writeSeq ?? null)
}
